import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { getCompanyTokenUsage } from '@/store/chat/actions';

import { selectTokenUsage, selectTokenUsageErrors, selectTokenUsageLoading } from '@/store/chat/selectors';

import { Card, Col, Modal, Pagination, Progress, Row, Space, Statistic, Table, Typography } from 'antd';
import { FiClock, FiDollarSign, FiTrendingUp, FiUsers } from 'react-icons/fi';

import dayjs from 'dayjs';

import styles from './TokenUsageModal.module.scss';

const { Title, Text } = Typography;

const TokenUsageModal = ({ visible, onClose, tokenQuota }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const { t } = useTranslation('chat');

  const dispatch = useDispatch();
  const tokenUsage = useSelector(selectTokenUsage);
  const loading = useSelector(selectTokenUsageLoading);
  const errors = useSelector(selectTokenUsageErrors);

  // Загружаем данные об использовании токенов при открытии модала
  useEffect(() => {
    if (visible) {
      dispatch(getCompanyTokenUsage(currentPage, 20));
    }
  }, [visible, currentPage, dispatch]);

  const handlePageChange = page => {
    setCurrentPage(page);
  };
  // Форматируем числа для отображения
  const formatTokenCount = count => {
    const numericCount = parseInt(count) || 0;
    if (numericCount >= 1000000) {
      return `${(numericCount / 1000000).toFixed(1)}M`;
    }
    if (numericCount >= 1000) {
      return `${(numericCount / 1000).toFixed(1)}K`;
    }
    return numericCount.toLocaleString();
  }; // Форматируем стоимость
  const formatCost = cost => {
    const numericCost = parseFloat(cost) || 0;
    return `${numericCost.toFixed(2)} ₽`;
  };

  // Определяем цвет прогресс-бара
  const getProgressColor = percent => {
    if (percent >= 90) return '#ff4d4f';
    if (percent >= 75) return '#faad14';
    return '#52c41a';
  };

  const percentage =
    tokenQuota.global_limit > 0 ? Math.round((tokenQuota.global_used / tokenQuota.global_limit) * 100) : 0;

  const remainingTokens = tokenQuota.global_limit - tokenQuota.global_used;

  // Конфигурация колонок таблицы
  const columns = [
    {
      title: t('tokenUsage.table.user'),
      dataIndex: 'user_name',
      key: 'user_name',
      render: user_name => <Text strong>{user_name}</Text>,
    },
    {
      title: 'id',
      dataIndex: 'user_id',
      key: 'user_id',
      render: userId => <Text strong>{userId}</Text>,
    },
    {
      title: t('tokenUsage.table.session'),
      dataIndex: 'session_id',
      key: 'session_id',
      render: sessionId => (
        <Text code style={{ fontSize: '12px' }}>
          {sessionId ? String(sessionId).substring(0, 8) : '-'}...
        </Text>
      ),
    },
    // {
    //   title: t('tokenUsage.table.inputTokens'),
    //   dataIndex: 'input_tokens',
    //   key: 'input_tokens',
    //   align: 'right',
    //   render: tokens => formatTokenCount(tokens),
    // },
    // {
    //   title: t('tokenUsage.table.outputTokens'),
    //   dataIndex: 'output_tokens',
    //   key: 'output_tokens',
    //   align: 'right',
    //   render: tokens => formatTokenCount(tokens),
    // },
    {
      title: t('tokenUsage.table.totalTokens'),
      dataIndex: 'total_tokens',
      key: 'total_tokens',
      align: 'right',
      render: tokens => <Text strong>{formatTokenCount(tokens)}</Text>,
    },
    // {
    //   title: t('tokenUsage.table.cost'),
    //   dataIndex: 'cost',
    //   key: 'cost',
    //   align: 'right',
    //   render: cost => (
    //     <Text strong style={{ color: '#1890ff' }}>
    //       {formatCost(cost)}
    //     </Text>
    //   ),
    // },
    {
      title: t('tokenUsage.table.time'),
      dataIndex: 'recorded_at',
      key: 'recorded_at',
      render: time => <Text type='secondary'>{time ? dayjs(time).format('DD.MM.YYYY HH:mm') : '-'}</Text>,
    },
  ];

  return (
    <Modal
      title={t('tokenUsage.modal.title')}
      open={visible}
      onCancel={onClose}
      footer={null}
      width={1000}
      className={styles.tokenModal}
    >
      <div className={styles.modalContent}>
        {/* Общая статистика */}
        <Row gutter={[16, 16]} className={styles.statsRow}>
          <Col span={8}>
            <Card size='small'>
              <Statistic
                title={t('tokenUsage.stats.totalLimit')}
                value={formatTokenCount(tokenQuota.global_limit)}
                prefix={<FiTrendingUp />}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card size='small'>
              <Statistic
                title={t('tokenUsage.stats.used')}
                value={formatTokenCount(tokenQuota.global_used)}
                prefix={<FiUsers />}
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card size='small'>
              <Statistic
                title={t('tokenUsage.stats.remaining')}
                value={formatTokenCount(remainingTokens)}
                prefix={<FiTrendingUp />}
                valueStyle={{ color: remainingTokens > 0 ? '#52c41a' : '#ff4d4f' }}
              />
            </Card>
          </Col>
          {/* <Col span={6}>
            <Card size='small'>
              <Statistic
                title={t('tokenUsage.stats.totalCost')}
                value={formatCost(tokenQuota.global_cost)}
                valueStyle={{ color: '#faad14' }}
              />
            </Card>
          </Col> */}
        </Row>

        {/* Прогресс-бар */}
        <Card size='small' className={styles.progressCard}>
          <div className={styles.progressSection}>
            <div className={styles.progressHeader}>
              <Title level={5} className={styles.progressTitle}>
                {t('tokenUsage.progress.title')}
              </Title>
              <Text className={styles.progressText}>
                {percentage}% {t('tokenUsage.progress.used')}
              </Text>
            </div>
            <Progress
              percent={percentage}
              strokeColor={getProgressColor(percentage)}
              trailColor='#f0f0f0'
              strokeWidth={12}
              showInfo={false}
            />
            <div className={styles.progressLabels}>
              <Text type='secondary'>0</Text>
              <Text type='secondary'>{formatTokenCount(tokenQuota.global_limit)}</Text>
            </div>
          </div>
        </Card>

        {/* Информация о последнем обновлении */}
        {tokenQuota.last_updated && (
          <Card size='small' className={styles.updateInfo}>
            <Space>
              <FiClock />{' '}
              <Text type='secondary'>
                {t('tokenUsage.lastUpdated')}:{' '}
                {tokenQuota.last_updated ? dayjs(tokenQuota.last_updated).format('DD.MM.YYYY HH:mm:ss') : '-'}
              </Text>
            </Space>
          </Card>
        )}

        {/* Таблица с историей использования */}
        <Card size='small' title={t('tokenUsage.table.title')} className={styles.tableCard}>
          <Table
            columns={columns}
            dataSource={tokenUsage.items}
            rowKey={record => `${record.user_id}-${record.session_id}-${record.recorded_at}`}
            loading={loading.getCompanyTokenUsage}
            pagination={false}
            size='small'
            scroll={{ x: 800 }}
          />

          {tokenUsage.total > 0 && (
            <div className={styles.paginationContainer}>
              <Pagination
                current={currentPage}
                total={tokenUsage.total}
                pageSize={tokenUsage.limit}
                onChange={handlePageChange}
                showSizeChanger={false}
                showQuickJumper
                showTotal={(total, range) =>
                  t('tokenUsage.pagination.total', {
                    start: range[0],
                    end: range[1],
                    total,
                  })
                }
              />
            </div>
          )}
        </Card>
      </div>
    </Modal>
  );
};

export default TokenUsageModal;
